import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Box, Drawer, Typography, Popper, useMediaQuery } from "@mui/material";
import Button from "../Button";
import Input from "../Input";
import { useTranslation } from "react-i18next";
import CloseIcon from "assets/icons/close.svg";
import moment from "moment";
import { useScrollLock } from "hooks/useScrollLock";

export default function TimeDropdown30Min({
  value,
  onChange,
  InputFieldProps,
  customLabelStyles = true,
  labelShrink,
  onOpen,
  onClose,
  isDropoff = false, // ADD THIS
  pickupTime, // ADD THIS
  pickupDate, // ADD THIS
  onAutoCorrect, // ADD THIS NEW PROP
  onSelectionComplete,
  renderPickerOnly = false,
  disableInternalOpen = false,
  triggerOnlyProps,
  ...rest
}: any) {
  const shouldShrink = labelShrink !== undefined ? labelShrink : undefined;
  const isSmallScreen = useMediaQuery("(max-width:899.95px)");
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [timeValue, setTimeValue] = useState<string>(value || "");

  const InputProps = {
    autoComplete: "off",
    id: "time",
    ...InputFieldProps,
    error: InputFieldProps?.helperText ? InputFieldProps?.error : false,
    customLabelStyles: customLabelStyles,
    inputProps: {
      ...InputFieldProps?.inputProps,
      shrink: timeValue ? true : shouldShrink,
      readOnly: true,
      inputMode: "none" as const,
    },
  };
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentInputId = InputFieldProps?.id || "time"; // Get the current input ID
  const closeDrawer = useCallback(() => {
    if (isSmallScreen) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      (document.getElementById(currentInputId) as HTMLElement | null)?.blur?.();
    }
    setOpen(false);
    onClose?.();
  }, [currentInputId, isSmallScreen, onClose]);

  useScrollLock(!isSmallScreen && open);

  useEffect(() => {
    return () => {
      (document.getElementById(currentInputId) as HTMLElement | null)?.blur?.();
      (document.activeElement as HTMLElement | null)?.blur?.();
    };
  }, [currentInputId]);

  const isTimeDisabled = (
    timeString: string,
    selectedDate?: string,
    pickupTime?: string,
    pickupDate?: string,
    isDropoff?: boolean
  ): boolean => {
    const today = moment().format("YYYY-MM-DD");

    // Convert the time string to 24-hour format for comparison
    const timeDate = moment(
      `${selectedDate} ${timeString}`,
      "YYYY-MM-DD hh:mm A"
    );

    // ADD THIS: Disable times outside 8 AM - 8 PM range
    const hour = timeDate.hour();
    if (hour < 8 || hour > 20 || (hour === 20 && timeDate.minute() > 0)) {
      return true;
    }

    // For pickup time on today's date
    if (selectedDate === today && !isDropoff) {
      // Disable past times + next 2 hours
      const twoHoursFromNow = moment().add(2, "hours");
      return timeDate.isBefore(twoHoursFromNow, "minute");
    }

    // For dropoff time when pickup and dropoff dates are the same
    if (
      isDropoff &&
      pickupDate &&
      selectedDate &&
      pickupDate === selectedDate &&
      pickupTime
    ) {
      const pickupTimeMoment = moment(
        `${pickupDate} ${pickupTime}`,
        "YYYY-MM-DD hh:mm A"
      );
      const minDropoffTime = pickupTimeMoment.add(0.5, "minutes");
      return timeDate.isSameOrBefore(minDropoffTime, "minute");
    }

    return false;
  };
  const generateTimeSlots = () => {
    const slots: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        const timeString = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();
  // ADD THIS after generateTimeSlots():
  const availableTimeSlots = timeSlots.filter((time) => {
    return !isTimeDisabled(
      time,
      rest?.selectedDate,
      pickupTime,
      pickupDate,
      isDropoff
    );
  });

  // Convert 24-hour format (HH:MM) to 12-hour format (HH:MM AM/PM)
  const formatTimeForDisplay = (time24: string): string => {
    if (!time24) return "";
    const time = moment(time24, "HH:mm").format("hh:mm A");
    return time;
  };

  // Convert 12-hour format (HH:MM AM/PM) to 24-hour format (HH:MM)
  const convertTo24Hour = (time12: string): string => {
    if (!time12) return "";
    const time = moment(time12, "hh:mm A").format("HH:mm");
    return time;
  };

  // Add this helper function at the top level (outside component)
  // const addHoursToTime = (timeString: string, hoursToAdd: number): string => {
  //   const [time, period] = timeString.split(" ");
  //   const [hours, minutes] = time.split(":");
  //   let hour = parseInt(hours, 10);

  //   if (period === "PM" && hour !== 12) {
  //     hour += 12;
  //   } else if (period === "AM" && hour === 12) {
  //     hour = 0;
  //   }

  //   const date = new Date();
  //   date.setHours(hour, parseInt(minutes, 10), 0, 0);
  //   date.setHours(date.getHours() + hoursToAdd);

  //   return date.toLocaleTimeString("en-US", {
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     hour12: true,
  //   });
  // };

  useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        // If value is in 24-hour format, convert to display format
        if (
          value.includes(":") &&
          !value.includes("AM") &&
          !value.includes("PM")
        ) {
          const displayTime = formatTimeForDisplay(value);
          setTimeValue(displayTime);
        } else {
          setTimeValue(value);
        }
      }
    } else {
      setTimeValue("");
    }
  }, [value]);

  const handleTimeSelect = (selectedTime: string) => {
    setTimeValue(selectedTime);

    // Convert to 24-hour format for storage
    const time24 = convertTo24Hour(selectedTime);

    if (onChange) {
      onChange(time24); // Store in 24-hour format
    }

    if (renderPickerOnly && onSelectionComplete) {
      window.setTimeout(() => {
        onSelectionComplete();
      }, 0);
      return;
    }

    closeDrawer();
  };

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const currentInput = document.getElementById(currentInputId);
      const dropdown = document.getElementById("time-dropdown-30min");

      // Close if clicking outside both dropdown and input
      if (
        dropdown &&
        currentInput &&
        !dropdown.contains(target) &&
        !currentInput.contains(target)
      ) {
        closeDrawer();
      }
    },
    [closeDrawer, currentInputId]
  );
  useEffect(() => {
    if (open) {
      // Close all other time dropdowns
      const allInputs = ["time", "time_delivery", "endTime_delivery"];
      allInputs.forEach((inputId) => {
        if (inputId !== currentInputId) {
          const input = document.getElementById(inputId) as any;
          if (input && input._reactFiber?.memoizedProps?.onClick) {
            // Trigger blur to close other dropdowns
            input.blur();
          }
        }
      });

      if (onOpen) onOpen();
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleOutsideClick);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [open, currentInputId, closeDrawer, handleOutsideClick, onOpen]);

  const scrollToSelected = useCallback(() => {
    if (containerRef.current) {
      let selectedIndex = -1;

      // Generate available slots first
      const availableSlots = timeSlots.filter((slot) => {
        return !isTimeDisabled(
          slot,
          rest?.selectedDate,
          pickupTime,
          pickupDate,
          isDropoff
        );
      });

      // If there's a value, find the selected time in available slots
      if (timeValue || value) {
        selectedIndex = availableSlots.findIndex((slot) => {
          const slot24 = convertTo24Hour(slot);
          const value24 =
            value && typeof value === "string"
              ? value.includes("AM") || value.includes("PM")
                ? convertTo24Hour(value)
                : value
              : "";
          const timeValue24 = timeValue ? convertTo24Hour(timeValue) : "";

          return (
            slot === timeValue ||
            slot === value ||
            slot24 === value24 ||
            slot24 === timeValue24
          );
        });
      }

      // If no value selected or not found, scroll to first item (index 0)
      if (selectedIndex === -1) {
        selectedIndex = 0;
      }

      if (selectedIndex !== -1 && availableSlots.length > 0) {
        const buttons = containerRef.current.querySelectorAll("button");
        const itemHeight = buttons.length > 0 ? buttons[0].offsetHeight : 48;

        const scrollPosition =
          selectedIndex * itemHeight -
          containerRef.current.clientHeight / 2 +
          itemHeight / 2;

        containerRef.current.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    }
  }, [timeSlots, rest?.selectedDate, pickupTime, pickupDate, isDropoff, timeValue, value]);

  // Update the useEffect with longer delay:
  useEffect(() => {
    if (open) {
      setTimeout(scrollToSelected, 300); // Longer delay to ensure buttons are rendered
    }
  }, [
    open,
    timeValue,
    value,
    rest?.selectedDate,
    pickupTime,
    pickupDate,
    isDropoff,
  ]);

  // MODIFY the auto-correction useEffect:
  useEffect(() => {
    if (value && rest?.selectedDate) {
      const currentTime =
        typeof value === "string"
          ? value.includes("AM") || value.includes("PM")
            ? value
            : formatTimeForDisplay(value)
          : "";

      const isCurrentTimeValid = availableTimeSlots.some((slot) => {
        const slot24 = convertTo24Hour(slot);
        const value24 = convertTo24Hour(currentTime);
        return slot === currentTime || slot24 === value24;
      });

      // If current time is no longer valid, auto-select appropriate time
      if (!isCurrentTimeValid && availableTimeSlots.length > 0) {
        // SIGNAL THAT AUTO-CORRECTION IS HAPPENING
        if (onAutoCorrect) {
          onAutoCorrect(true);
        }

        let timeToSelect = availableTimeSlots[0];
        const today = moment().format("YYYY-MM-DD");

        if (rest?.selectedDate === today && !isDropoff) {
          const now = moment();
          const twoHoursFromNow = now.add(2, "hours");

          const nextAvailableSlot = availableTimeSlots.find((slot) => {
            const slotTime = moment(
              `${rest.selectedDate} ${slot}`,
              "YYYY-MM-DD hh:mm A"
            );
            return slotTime.isSameOrAfter(twoHoursFromNow);
          });

          if (nextAvailableSlot) {
            timeToSelect = nextAvailableSlot;
          }
        }

        const time24 = convertTo24Hour(timeToSelect);

        setTimeValue(timeToSelect);
        if (onChange) {
          onChange(time24);
        }

        // RESET THE FLAG AFTER UPDATE
        setTimeout(() => {
          if (onAutoCorrect) {
            onAutoCorrect(false);
          }
        }, 200);
      } else if (!isCurrentTimeValid && availableTimeSlots.length === 0) {
        if (onAutoCorrect) {
          onAutoCorrect(true);
        }

        setTimeValue("");
        if (onChange) {
          onChange("");
        }

        setTimeout(() => {
          if (onAutoCorrect) {
            onAutoCorrect(false);
          }
        }, 200);
      }
    }
  }, [
    rest?.selectedDate,
    pickupTime,
    pickupDate,
    isDropoff,
    availableTimeSlots.length,
  ]);

  const renderPickerContent = () => (
    <Box
      id="time-dropdown-30min"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        minHeight: 0,
        bgcolor: "#ffffff",
      }}
    >
      <Typography component={"p"} textAlign={"center"} fontSize={16} fontWeight={700} mb={2}>
        {t("pick_time") || "Select Time"}
      </Typography>
      <Box
        ref={containerRef}
        data-scroll-lock-allow="true"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          "&::-webkit-scrollbar": {
            width: "8px",
            opacity: 0,
            transition: "opacity 0.3s ease",
          },
          "&:hover::-webkit-scrollbar": {
            opacity: 1,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#520A76",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
          },
          "-ms-overflow-style": "auto",
          scrollbarWidth: "auto",
        }}
      >
        {availableTimeSlots.map((time) => {
          const time24 = convertTo24Hour(time);
          const value24 =
            value && typeof value === "string"
              ? value.includes("AM") || value.includes("PM")
                ? convertTo24Hour(value)
                : value
              : timeValue
                ? convertTo24Hour(timeValue)
                : "";
          const disabled = isTimeDisabled(
            time,
            rest?.selectedDate,
            pickupTime,
            pickupDate,
            isDropoff,
          );
          const isSelected = time === timeValue || time24 === value24;

          return (
            <Button
              key={time}
              fullWidth
              disabled={disabled}
              variant={isSelected ? "contained" : "text"}
              sx={{
                mb: 1,
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 500,
                color: disabled ? "#CCCCCC" : isSelected ? "#fff" : "#0A0A0E",
                backgroundColor: disabled
                  ? "#F0F0F0"
                  : isSelected
                    ? "#520A76"
                    : "transparent",
                cursor: disabled ? "not-allowed" : "pointer",
                ":hover": {
                  backgroundColor: isSelected ? "#520A76" : "#F9F9F9",
                },
              }}
              onClick={() => !disabled && handleTimeSelect(time)}
            >
              {time}
            </Button>
          );
        })}
      </Box>
    </Box>
  );

  if (renderPickerOnly) {
    return renderPickerContent();
  }

  return (
    <>
      <Box position="relative" ref={anchorRef}>
        <Input
          {...rest}
          {...InputProps}
          customLabelStyles={customLabelStyles}
          placeholder={t("select_time") || "Select Time"}
          onMouseDown={
            triggerOnlyProps?.onMouseDown ||
            ((e: ReactMouseEvent) => e.preventDefault())
          }
          onFocusCapture={triggerOnlyProps?.onFocusCapture}
          onClick={
            triggerOnlyProps?.onClick ||
            (() => {
              if (!disableInternalOpen) {
                setOpen(true);
              }
            })
          }
          onKeyDown={triggerOnlyProps?.onKeyDown}
          readOnly
          value={
            timeValue ||
            (typeof value === "string" ? formatTimeForDisplay(value) : "")
          }
          InputLabelProps={{
            shrink: shouldShrink !== undefined ? shouldShrink : !!(timeValue || value),
          }}
          inputProps={{
            ...InputProps.inputProps,
            ...triggerOnlyProps?.inputProps,
          }}
        />
        {open &&
          (isSmallScreen ? (
            <DrawerComponent
              open={open}
              onClose={closeDrawer}
              isMobile={isSmallScreen}
            >
              <Box
                width="100%"
                boxShadow={0}
                zIndex={1900}
                borderRadius={"12px"}
                mt={1}
                p={2}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                {renderPickerContent()}
              </Box>
            </DrawerComponent>
          ) : (
            <Popper
              open={open && !isSmallScreen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
              style={{ zIndex: 1500 }}
            >
              <Box
                id="time-dropdown-30min"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.3)",
                  width: anchorRef.current?.offsetWidth || "auto",
                  maxWidth: anchorRef.current?.offsetWidth || "300px",
                  maxHeight: "400px",
                }}
              >
                <Typography
                  component={"p"}
                  textAlign={"left"}
                  fontSize={14}
                  fontWeight={700}
                  mb={1}
                  pt={1.5}
                  ml={2}
                >
                  {t("time") || "Select Time"}
                </Typography>
                <Box
                  maxHeight={"300px"}
                  overflow={"auto"}
                  ref={containerRef}
                  data-scroll-lock-allow="true"
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "8px", // Adjust width as needed
                      opacity: 0, // Make it invisible by default
                      transition: "opacity 0.3s ease", // Smooth transition for opacity change
                    },
                    "&:hover::-webkit-scrollbar": {
                      opacity: 1, // Show scrollbar on hover or when scrolling
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#520A76", // Customize the scrollbar thumb color
                      borderRadius: "10px", // Optional: Customize thumb border radius
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f0f0f0", // Optional: Customize the scrollbar track
                    },
                    "-ms-overflow-style": "auto", // Make scrollbar visible on IE/Edge
                    "scrollbar-width": "auto", // Make scrollbar visible in Firefox
                  }}
                >
                  {/* // In Popper section: */}
                  {availableTimeSlots.map((time) => {
                    const time24 = convertTo24Hour(time);
                    const value24 =
                      value && typeof value === "string"
                        ? value.includes("AM") || value.includes("PM")
                          ? convertTo24Hour(value)
                          : value
                        : timeValue
                          ? convertTo24Hour(timeValue)
                          : "";
                    const isSelected = time === timeValue || time24 === value24;
                    const disabled = isTimeDisabled(
                      time,
                      rest?.selectedDate,
                      pickupTime,
                      pickupDate,
                      isDropoff
                    ); // UPDATED THIS LINE

                    return (
                      <Button
                        key={time}
                        fullWidth
                        disabled={disabled} // ADD THIS
                        variant={isSelected ? "text" : "text"}
                        sx={{
                          // mb: 1,
                          pl: 2,
                          borderRadius: 0,
                          fontSize: 12,
                          fontWeight: 600,
                          color: disabled
                            ? "#CCCCCC"
                            : isSelected
                              ? "#fff"
                              : "#0A0A0E", // UPDATED THIS
                          textAlign: "left", // ADD THIS LINE
                          justifyContent: "flex-start", // ADD THIS LINE
                          backgroundColor: disabled
                            ? "#F0F0F0"
                            : isSelected
                              ? "#520A76"
                              : "transparent", // UPDATED THIS
                          ":hover": {
                            backgroundColor: isSelected
                              ? "#520A76"
                              : "#520A761A",
                          },
                        }}
                        onClick={() => !disabled && handleTimeSelect(time)} // UPDATED THIS
                      >
                        {time}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Popper>
          ))}
      </Box>
    </>
  );
}

const DrawerComponent = ({
  open,
  onClose,
  isMobile,
  children,
}: {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  children: React.ReactNode;
}) => {
  const { t, i18n } = useTranslation();
  const handleClose = () => {
    if (isMobile) {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    onClose();
  };
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      ModalProps={
        isMobile
          ? {
              disableAutoFocus: true,
              disableEnforceFocus: true,
              disableRestoreFocus: true,
              disableScrollLock: true,
            }
          : undefined
      }
      PaperProps={{
        "data-scroll-lock-allow": "true",
        style: {
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: "16px",
          height: "min(90dvh, 720px)",
          maxHeight: "90dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      sx={{ zIndex: "1400 !important" }}
    >
      <Box
        component={"img"}
        src={CloseIcon}
        alt="X"
        top={"16px"}
        position={"absolute"}
        onClick={handleClose}
        right={i18n.language === "ar" ? "auto" : "16px"}
        left={i18n.language === "ar" ? "16px" : "auto"}
        sx={{ cursor: "pointer" }}
      />
      <Box sx={{ textAlign: "center", mb: 2, flexShrink: 0 }}>
        <h3 style={{ margin: 0, fontSize: "16px" }}>
          {t("time") || "Select Time"}
        </h3>
      </Box>
      {children}
    </Drawer>
  );
};
